import styled, { css } from 'styled-components';
import { rem } from 'polished';
import {
    space,
    color,
    typography,
    SpaceProps,
    ColorProps,
    TypographyProps,
    system,
    borderRadius,
    width,
    maxHeight,
    maxWidth,
    height,
    flexbox,
    flex,
} from 'styled-system';
import { theme } from '../../../config/theme';
import { fluidCSSPropertyHelper } from '../../utils/fluidCSSProperty';
import pseudoStyle from '../../utils/pseudoStyle';

interface TextProps extends SpaceProps, ColorProps, TypographyProps {
    accent?: boolean;
    light?: boolean;
    bold?: boolean;
    italic?: boolean;
    textTransform?: 'capitalize' | 'lowercase' | 'uppercase';
    whiteSpace?: string;
    wordBreak?: 'normal' | 'break-all' | 'break-word' | 'keep-all';
    textDecoration?: string;
    cursor?: string;
}

const themeKeys = {
    color: 'colors',
    backgroundColor: 'colors',
    borderColor: 'colors',
    boxShadow: 'shadows',
    cursor: 'pointer',
};

const hover = pseudoStyle({
    prop: '_hover',
    pseudoclass: 'hover',
    keys: themeKeys,
});

const Text = styled.span<TextProps>`
    color: currentColor;
    font-weight: ${({ bold }) => (bold ? '700' : '400')};
    transition: all 0.2s ease-out;
    ${borderRadius};
    ${height};
    ${flexbox};
    ${flex};
    ${width};
    ${maxHeight};
    ${maxWidth};
    ${space};
    ${color};
    ${typography}
    ${system({
        whiteSpace: true,
        wordBreak: true,
        textDecoration: true,
        cursor: true,
    })}
    ${hover}

    ${({ textTransform }) =>
        textTransform &&
        css`
            text-transform: ${textTransform};
        `}

    ${({ italic }) =>
        italic &&
        css`
            font-style: italic;
        `}
`;

const Head1 = styled(Text)`
    ${fluidCSSPropertyHelper({
        prop: 'font-size',
        range: ['28px', '46px', '56px'],
    })}
    line-height: 1.2;
    font-weight: 600;
    font-family: Outfit, system-font;
`;
Head1.defaultProps = {
    as: 'h1',
};

const Head2 = styled(Text)`
    ${fluidCSSPropertyHelper({
        prop: 'font-size',
        range: ['26px', '38px', '48px'],
    })}
    line-height: 1.2;
    font-weight: 600;
    font-family: Outfit, system-font;
`;
Head2.defaultProps = {
    as: 'h2',
};

const Head3 = styled(Text)`
    ${fluidCSSPropertyHelper({
        prop: 'font-size',
        range: ['22px', '30px', '40px'],
    })}
    line-height: 1.2;
    font-weight: 600;
    font-family: Outfit, system-font;
`;
Head3.defaultProps = {
    as: 'h3',
};

const Head4 = styled(Text)`
    ${fluidCSSPropertyHelper({
        prop: 'font-size',
        range: ['19px', '22px', '32px'],
    })}
    line-height: 1.2;
    font-weight: 600;
    font-family: Outfit, system-font;
`;
Head4.defaultProps = {
    as: 'h4',
};

const Head5 = styled(Text)`
    font-size: ${rem('28px')};
    line-height: 1.3;
    font-weight: 600;
    font-family: Outfit, system-font;
`;
Head5.defaultProps = {
    as: 'h5',
};

const Head6 = styled(Text)`
    ${fluidCSSPropertyHelper({
        prop: 'font-size',
        range: ['18px', '20px', '24px'],
    })}
    line-height: 1.3;
    font-weight: 600;
    font-family: Outfit, system-font;
`;
Head6.defaultProps = {
    as: 'h6',
};

const Subheading1 = styled(Text)`
    font-size: ${rem('20px')};
    line-height: 1.3;
    font-family: Outfit, system-font;
`;
Subheading1.defaultProps = {
    as: 'h6',
};

const Headline = styled(Text)`
    font-size: ${rem('18px')};
    line-height: 1.3;
    font-family: ${({ theme }) => theme.fonts.heading};
`;
Headline.defaultProps = {
    as: 'h6',
};

const Body1 = styled(Text)`
    font-size: ${rem('16px')};
    font-weight: ${({ bold }) => (bold ? '700' : '400')};
    line-height: 1.5;
`;
Body1.defaultProps = {
    as: 'p',
};

const Body2 = styled(Text)`
    font-size: ${rem('14px')};
    font-weight: ${({ bold }) => (bold ? '700' : '400')};
    line-height: 1.5;
`;
Body2.defaultProps = {
    as: 'p',
};

const Caption = styled(Text)`
    font-size: ${rem('12px')};
    font-weight: ${({ bold }) => (bold ? '700' : '400')};
    line-height: ${({ lineHeight = '1.3' }) => lineHeight};
`;
Caption.defaultProps = {
    as: 'span',
};

export {
    Text,
    Head1,
    Head2,
    Head3,
    Head4,
    Head5,
    Head6,
    Headline,
    Subheading1,
    Body1,
    Body2,
    Caption,
};
